/**
 * Terms (Aspen calls them marking periods), and the questions we ask of them.
 *
 * ## Why this file exists
 *
 * Updraft has been working around not knowing what term it is. `docs/honors.md`
 * says plainly that "the SIS import gives us term *codes* and never the dates
 * they run", and `currentHonorsTerm` spreads the term list evenly across a
 * September-to-June year as a result — right except around a term boundary.
 *
 * That was never true. `marking_periods.csv` is a declared, parsed, imported
 * file carrying `start_date` and `end_date`, and `importMarkingPeriodsCsv` has
 * been writing it to a `markingPeriods` collection all along. The client simply
 * cannot read it: that collection has no Firestore rule, so it falls through to
 * the default deny-all, and it is a collection rather than a rollup document,
 * which the project's read budget would not want anyway.
 *
 * So this is not new data. It is a rollup, a rule, and the vocabulary for using
 * what was already there.
 *
 * ## The two things to be careful about
 *
 * 1. **A date is usually in more than one term, and terms may overlap without
 *    nesting.** Full-year sections run alongside semesters, so 12 November is
 *    in both FY and S1 — that much is nesting. But a school runs several term
 *    *schemes* at once: advisory on FY, academic courses on S1/S2, and in the
 *    MS specials on quarters while main courses run semesters or full year. So
 *    on a given Tuesday the MS is simultaneously in Q3, S2 and FY, and those
 *    are three different right answers depending on which course you mean.
 *    Overlap is also not safely assumed to be containment: a progress-report
 *    period can straddle a semester boundary.
 *
 *    `termsContaining` is therefore the honest API: it returns *every* term
 *    covering a date, narrowest first. `currentTerm` is the convenience on top
 *    for the places that must show one answer, and its tie-break (narrowest,
 *    then id) is a display choice, not a fact about the calendar.
 *
 *    **The term is a property of the section, not of the day.** Anything that
 *    has to be correct — a policy window, a report boundary, a grade period —
 *    should start from a section's term code and call `termWindow`, never ask
 *    what term it is. "What term is it" is only ever a default to preselect.
 * 2. **The join between sections and terms is not guaranteed.** Sections carry
 *    a term *code* (`SectionInfo.term`, e.g. "S1"); marking periods carry an
 *    `abbreviation`. They are expected to be the same string and nothing
 *    enforces it. `resolveTerm` tries abbreviation, then id, then name, all
 *    case-insensitively, and `unmatchedTermCodes` exists so a mismatch is
 *    reportable rather than silent.
 *
 * Every function here treats missing or unparseable dates as "unknown" rather
 * than guessing, and every caller is expected to keep its existing fallback.
 * Dates are compared as `YYYY-MM-DD` strings: they sort lexically, a term runs
 * to the end of its last day, and a millisecond timestamp would only invite
 * off-by-one errors around midnight and DST.
 *
 * ## Keep this module import-free
 *
 * It has no imports and should keep none. `functions/src/scripts/*` run under
 * `ts-node --transpile-only`, which until recently could not resolve the `.js`
 * specifiers the rest of `shared/` uses for sibling imports (fine under tsc and
 * Vite, fatal here -- see the note in `importers.ts` about the schemas barrel).
 * `functions/tsconfig.json` now turns on ts-node's `experimentalResolver`,
 * which does resolve them, so this is no longer the hard wall it was; staying a
 * leaf is still the cheapest way for a script to import these functions without
 * depending on that. `localIsoDate` lives here rather than in `honors.ts` for
 * exactly that reason, on top of being a date helper for term questions in the
 * first place.
 */

/**
 * One marking period as the SIS reports it.
 *
 * Moved here from `lookups.ts`, which declared the shape but gave it nothing to
 * do. Re-exported there so existing imports keep working.
 */
export interface MarkingPeriod {
  marking_period_id: string;
  name: string;
  /** The code sections use — "S1", "T2", "FY". Matches marking-periods.csv. */
  abbreviation: string;
  /** ISO 8601 `YYYY-MM-DD`. Empty when the export did not carry one. */
  start_date: string;
  /** ISO 8601 `YYYY-MM-DD`, inclusive. Empty when the export did not carry one. */
  end_date: string;
  school_id: string;
  /**
   * Where this record came from. Absent means the SIS import wrote it.
   *
   * `manual` marks a term somebody entered by hand because the export did not
   * deliver one. It is kept as a field rather than a separate document so that
   * every reader sees one list; the importer must never overwrite a manual
   * entry, which `mergeTermLookups` is responsible for.
   */
  source?: "sis" | "manual";
}

/** Keyed by `marking_period_id`. Stored at `lookups/marking_periods`. */
export interface TermLookup {
  [marking_period_id: string]: MarkingPeriod;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Today as `YYYY-MM-DD` in the viewer's own timezone.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that is UTC, so after 7pm
 * Eastern it already reads as tomorrow. Being one day out is exactly the error
 * term dates exist to remove, and a term boundary is the one place it shows.
 */
export function localIsoDate(today: Date = new Date()): string {
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

/** True when both ends of the term are usable dates. */
export function termHasDates(term: MarkingPeriod): boolean {
  return (
    ISO_DATE.test(term.start_date ?? "") && ISO_DATE.test(term.end_date ?? "")
  );
}

/**
 * The school year a term belongs to, derived from its start date.
 *
 * Mirrors `computeSchoolYear` (July starts a new year) rather than importing
 * it, so this module stays free of grade-schema dependencies. Returns undefined
 * for an undated term instead of guessing.
 */
export function termSchoolYear(term: MarkingPeriod): string | undefined {
  if (!ISO_DATE.test(term.start_date ?? "")) return undefined;
  const [yearPart, monthPart] = term.start_date.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return undefined;
  const startYear = month >= 7 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
}

/** Inclusive length in days. Undefined when either end is missing. */
export function termLengthDays(term: MarkingPeriod): number | undefined {
  if (!termHasDates(term)) return undefined;
  const start = Date.parse(`${term.start_date}T00:00:00Z`);
  const end = Date.parse(`${term.end_date}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
    return undefined;
  return Math.round((end - start) / 86_400_000) + 1;
}

/** Builds the rollup document from parsed marking periods. */
export function termLookup(periods: readonly MarkingPeriod[]): TermLookup {
  const lookup: TermLookup = {};
  for (const period of periods) {
    const id = period.marking_period_id?.trim();
    if (!id) continue;
    lookup[id] = { ...period, marking_period_id: id };
  }
  return lookup;
}

/**
 * Folds a fresh SIS import onto the stored lookup, **keeping manual entries**.
 *
 * `lookups/*` documents in this project are merge accumulators that never clear
 * a field a writer omits (`docs/access.md` documents what that has cost us), so
 * a hand-entered term has to survive every nightly import by construction
 * rather than by luck. A manual entry yields to the SIS only when the import
 * actually carries the same marking period — at which point the real thing has
 * arrived and the stand-in is no longer wanted.
 */
export function mergeTermLookups(
  stored: TermLookup,
  imported: TermLookup,
): TermLookup {
  const merged: TermLookup = { ...imported };
  for (const [id, term] of Object.entries(stored)) {
    if (term.source === "manual" && !imported[id]) merged[id] = term;
  }
  return merged;
}

export interface TermFilter {
  schoolYear?: string;
  schoolId?: string;
}

/**
 * Dated terms matching a filter, in chronological order.
 *
 * Terms sharing a start date are ordered longest first, so a full year is
 * listed ahead of the semester that begins with it — containers before their
 * parts, which is how a person would read the list out. `termsContaining`
 * deliberately inverts that, because "what term is it" wants the narrowest
 * answer rather than the widest.
 *
 * Undated terms are dropped: everything below asks a date question, and a term
 * with no dates cannot answer one. Callers wanting the raw list should read the
 * lookup directly.
 */
export function termsFor(
  lookup: TermLookup,
  filter: TermFilter = {},
): MarkingPeriod[] {
  return Object.values(lookup)
    .filter(termHasDates)
    .filter((term) => !filter.schoolId || term.school_id === filter.schoolId)
    .filter(
      (term) =>
        !filter.schoolYear || termSchoolYear(term) === filter.schoolYear,
    )
    .sort(
      (a, b) =>
        a.start_date.localeCompare(b.start_date) ||
        b.end_date.localeCompare(a.end_date) ||
        a.marking_period_id.localeCompare(b.marking_period_id),
    );
}

/** True when `date` (YYYY-MM-DD) falls inside the term, both ends inclusive. */
export function termContains(term: MarkingPeriod, date: string): boolean {
  if (!termHasDates(term)) return false;
  return term.start_date <= date && date <= term.end_date;
}

/** Every term covering a date, shortest first. */
export function termsContaining(
  lookup: TermLookup,
  date: string,
  filter: TermFilter = {},
): MarkingPeriod[] {
  return termsFor(lookup, filter)
    .filter((term) => termContains(term, date))
    .sort((a, b) => {
      const lengthA = termLengthDays(a) ?? Number.MAX_SAFE_INTEGER;
      const lengthB = termLengthDays(b) ?? Number.MAX_SAFE_INTEGER;
      return (
        lengthA - lengthB ||
        a.marking_period_id.localeCompare(b.marking_period_id)
      );
    });
}

/**
 * The term we are actually in, or undefined when nothing covers the date.
 *
 * Shortest-first, so a November date reports S1 rather than FY. Undefined is a
 * real and common answer — summer, and the gap between terms at schools whose
 * marking periods do not abut — and callers must treat it as "I don't know"
 * rather than substituting the first or last term.
 */
export function currentTerm(
  lookup: TermLookup,
  today: string,
  filter: TermFilter = {},
): MarkingPeriod | undefined {
  return termsContaining(lookup, today, filter)[0];
}

/**
 * Matches a section's term code to a marking period.
 *
 * Tries `abbreviation`, then `marking_period_id`, then `name`, trimmed and
 * case-insensitive. Nothing guarantees sections and marking periods agree on
 * spelling, so this is deliberately forgiving — and `unmatchedTermCodes`
 * exists to surface the codes it still cannot place.
 */
export function resolveTerm(
  lookup: TermLookup,
  code: string,
  filter: TermFilter = {},
): MarkingPeriod | undefined {
  const wanted = code?.trim().toLowerCase();
  if (!wanted) return undefined;
  const candidates = termsFor(lookup, filter);
  const byField = (field: keyof MarkingPeriod) =>
    candidates.find(
      (term) =>
        String(term[field] ?? "")
          .trim()
          .toLowerCase() === wanted,
    );
  return (
    byField("abbreviation") ?? byField("marking_period_id") ?? byField("name")
  );
}

/** Start and end dates for a section term code, when we can place it. */
export function termWindow(
  lookup: TermLookup,
  code: string,
  filter: TermFilter = {},
): { start: string; end: string } | undefined {
  const term = resolveTerm(lookup, code, filter);
  return term ? { start: term.start_date, end: term.end_date } : undefined;
}

/**
 * Section term codes this lookup cannot place — the diagnostic for the join.
 *
 * A code here means anything term-windowed will silently fall back for those
 * sections, which is exactly the kind of failure this project has been bitten
 * by before. Report it; do not paper over it.
 */
export function unmatchedTermCodes(
  lookup: TermLookup,
  codes: readonly string[],
  filter: TermFilter = {},
): string[] {
  const unmatched = new Set<string>();
  for (const code of codes) {
    const trimmed = code?.trim();
    if (!trimmed) continue;
    if (!resolveTerm(lookup, trimmed, filter)) unmatched.add(trimmed);
  }
  return [...unmatched].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}
